import React from 'react';
import { FaBox, FaMoneyBillWave, FaToggleOn, FaTags } from 'react-icons/fa';

// Product Statistics Component
interface ProductStatsProps {
  totalProducts: number;
  averagePrice: string;
  productsWithOptions: number;
  uniqueCategories: number;
  hasValidPrices: boolean;
}

export const ProductStatistics: React.FC<ProductStatsProps> = ({
  totalProducts,
  averagePrice,
  productsWithOptions,
  uniqueCategories,
  hasValidPrices,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-blue-600 text-sm font-medium">Toplam Ürün</p>
          <p className="text-2xl font-bold text-blue-900">{totalProducts}</p>
        </div>
        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
          <FaBox className="text-white text-lg" />
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-green-600 text-sm font-medium">Ortalama Fiyat</p>
          <p className="text-2xl font-bold text-green-900">
            {hasValidPrices ? `${averagePrice} ₺` : 'Hesaplanamıyor'}
          </p>
          {!hasValidPrices && totalProducts > 0 && (
            <p className="text-xs text-green-500 mt-1">Geçerli fiyat bulunamadı</p>
          )}
        </div>
        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
          <FaMoneyBillWave className="text-white text-lg" />
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-purple-600 text-sm font-medium">Seçenekli Ürün</p>
          <p className="text-2xl font-bold text-purple-900">{productsWithOptions}</p>
        </div>
        <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
          <FaToggleOn className="text-white text-lg" />
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-orange-600 text-sm font-medium">Kategori Sayısı</p>
          <p className="text-2xl font-bold text-orange-900">{uniqueCategories}</p>
        </div>
        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
          <FaTags className="text-white text-lg" />
        </div>
      </div>
    </div>
  </div>
);

// Product Action Bar Component
interface ProductActionBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onShowAdd: () => void;
}

export const ProductActionBar: React.FC<ProductActionBarProps> = ({
  searchTerm,
  onSearchChange,
  onShowAdd,
}) => (
  <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-8">
    <div className="flex items-center gap-3 flex-1 max-w-md">
      <div className="relative flex-1">
        <svg
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Ürün adı, açıklama veya kategori ara..."
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </div>
    </div>

    <button
      onClick={onShowAdd}
      className="flex items-center gap-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      <span>Yeni Ürün Ekle</span>
    </button>
  </div>
);

// Product List Header Component
interface ProductListHeaderProps {
  count: number;
}

export const ProductListHeader: React.FC<ProductListHeaderProps> = ({ count }) => (
  <div className="p-6 border-b border-gray-100">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
      <FaBox className="text-blue-500" />
      Ürün Listesi ({count} ürün)
    </h3>
  </div>
);
